/**
 * Checklist widget factory — owns API→UI mapping, edit-callback wiring, and
 * checklist-specific config/state helpers. Re-entry detail files only register
 * or invoke this factory; they do not contain checklist implementation details.
 */

import { CHECKLIST_WIDGET_TYPE } from '../../constants/reentry-planning.constants';
import { isPlainObject } from '../../utils/plain-object.util';

export const isChecklistWidget = (widget) => widget?.widgetType === CHECKLIST_WIDGET_TYPE;

const readChecklistItemText = (item) => {
  if (typeof item?.text === 'string') {
    return item.text;
  }
  if (typeof item?.label === 'string') {
    return item.label;
  }
  return '';
};

const mapChecklistItem = (item) => ({
  id: typeof item?.id === 'string' ? item.id : String(item?.id ?? ''),
  text: readChecklistItemText(item),
  checked: Boolean(item?.checked),
});

/**
 * Maps API checklist config to Checklist / ChecklistWidgetBody props.
 * Defaults to readOnly; editable views attach callbacks via attachEditCallbacks.
 * Description is foundation chrome on the widget frame — not body props.
 */
export const mapChecklistWidgetProps = (widget, _reentryPlanItems = {}, options = {}) => {
  const config = isPlainObject(widget?.config) ? widget.config : {};
  const items = Array.isArray(config.items) ? config.items.map(mapChecklistItem) : [];
  const readOnly = options.readOnly !== false;

  return {
    id: widget?.id ? `checklist-${widget.id}` : 'checklist',
    title: typeof config.title === 'string' ? config.title : '',
    items,
    readOnly,
  };
};

/** Builds the widget config payload for PATCH (`{ title, items }` only — description is foundation). */
export const toChecklistWidgetConfig = (props = {}) => ({
  title: typeof props.title === 'string' ? props.title : '',
  items: Array.isArray(props.items)
    ? props.items.map((item) => ({
        id: item.id,
        text: item.text ?? '',
        checked: Boolean(item.checked),
      }))
    : [],
});

/**
 * Attaches mutation callbacks and clears readOnly for one editable checklist widget.
 * Handlers are keyed by widget id (API UUID) inside each callback.
 *
 * @param {object} widget - Mapped UI widget (`type` / `id` / `props`)
 * @param {object} handlers - Checklist mutation handlers from useChecklistWidgetMutations
 */
export const attachChecklistEditCallbacks = (widget, handlers) => ({
  ...widget,
  props: {
    ...widget.props,
    readOnly: false,
    onTitleBlur: (title) => handlers.onTitleBlur(widget.id, title),
    onSaveItem: (itemId, text) => handlers.onSaveItem(widget.id, itemId, text),
    onEditItem: (itemId, text) => handlers.onEditItem(widget.id, itemId, text),
    onToggleItem: (itemId, checked) => handlers.onToggleItem(widget.id, itemId, checked),
    onRemoveItem: (itemId) => handlers.onRemoveItem(widget.id, itemId),
  },
});

/** Replaces checklist props for one widget id across sections (local state after PATCH). */
export const replaceChecklistWidgetProps = (sections = [], widgetId, nextProps) =>
  sections.map((section) => ({
    ...section,
    widgets: (section.widgets ?? []).map((widget) => {
      if (widget.id !== widgetId || widget.type !== CHECKLIST_WIDGET_TYPE) {
        return widget;
      }
      return {
        ...widget,
        props: {
          ...widget.props,
          ...nextProps,
        },
      };
    }),
  }));

/**
 * Registry entry for section mapping and editable callback attachment.
 * Future widgets export the same shape from their own package.
 */
export const checklistWidgetFactory = {
  matches: isChecklistWidget,
  type: CHECKLIST_WIDGET_TYPE,
  mapProps: mapChecklistWidgetProps,
  attachEditCallbacks: attachChecklistEditCallbacks,
};
